<template>
  <v-row no-gutters>
    <v-col cols="12">
      <v-row no-gutters class="align-baseline px-3">
        <v-col cols="auto">
          <v-checkbox @change="changeLayerVisibility" v-model="isVisible" color="primary"> </v-checkbox>
        </v-col>
        <v-col cols="4" class="text-left text-body-2">
          {{ translatedLayerName }}
        </v-col>
        <v-col cols="5">
          <data-input
            v-model="selectedParameter"
            :items="computedParameterTypeRangesArray"
            return-object
            :data-type="dataType"
            :hint="$i18n.t('dialog.body.chooseParameter')"
            persistent-hint
          >
          </data-input>
        </v-col>
        <v-spacer />
        <v-col>
          <v-tooltip bottom color="font">
            <template #activator="{ on: onTooltip }">
              <v-btn small icon @click="changeLabelsVisibility" v-on="onTooltip">
                <v-icon
                  color="font"
                  style="height: 19px; width: 19px; transform: scale(1.3)"
                  v-text="isLabelsVisible ? '$labelActive' : '$label'"
                />
              </v-btn>
            </template>
            {{ isLabelsVisible ? $i18n.t('button.turnOffLabels') : $i18n.t('button.turnOnLabels') }}
          </v-tooltip>
        </v-col>
      </v-row>
      <v-row no-gutters class="justify-start px-3 mt-n3" v-if="computedRanges">
        <v-col cols="auto">
          <v-row no-gutters class="align-center" v-if="selectedParameter.abs">
            <v-col cols="auto" class="pr-1 ml-8" style="color: rgba(0,0,0,0.59)">
              <italic-subheader translation-path="sidebar.absoluteValues" class="pb-3" />
            </v-col>
          </v-row>
          <v-row no-gutters class="align-center" v-for="(range, index) in computedRanges" :key="range">
            <v-col cols="auto" class="pr-1 ml-8">
              <legend-layer-list-icon :color="colors[index]" :layer-type="geometryType"></legend-layer-list-icon>
            </v-col>
            <v-col cols="auto" class="text-body-2">
              {{ range }}
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
import { get } from 'vuex-pathify';
import { ranges, colors } from '@/assets/js/modelingResults';

import DataInput from '@/components/DataInput';
import ItalicSubheader from '@/components/ItalicSubheader';
import LegendLayerListIcon from '@/components/LegendLayerListIcon';

export default {
  name: 'ModelingResultsLayer',
  components: {
    DataInput,
    ItalicSubheader,
    LegendLayerListIcon,
  },
  props: {
    type: {
      type: String,
      required: true,
    },
    netType: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    isVisible: true,
    isLabelsVisible: false,
    selectedParameter: undefined,
    colors,
    dataType: {
      name: 'select',
    },
  }),
  computed: {
    rangesSettings: get(`admin/settingsValues@:rangesSettingName`),
    rangesSettingName() {
      return `${this.netType}_modeling_legend_ranges`;
    },
    geometryType() {
      return this.type === 'nodes' ? 'point' : 'linestring';
    },
    translatedLayerName() {
      return this.$i18n.t(`sidebar.${this.type}`);
    },
    parameterTypeRanges() {
      return ranges[this.netType][this.type];
    },
    parameterTypeRangesArray() {
      return this.$_objectToArray(this.parameterTypeRanges);
    },
    computedParameterTypeRangesArray() {
      return this.parameterTypeRangesArray.map(parameter => {
        const { name, unit } = parameter;
        return {
          ...parameter,
          text: `${this.$i18n.t(`sidebar.${name}`)} ${unit ? `[${unit}]` : ''}`,
          ranges: this.rangesSettings?.[this.type]?.[name]?.split(';') || parameter.ranges,
        };
      });
    },
    computedRanges() {
      if (!this.selectedParameter) {
        return false;
      }
      return this.prepareRanges(this.selectedParameter.ranges);
    },
  },
  watch: {
    selectedParameter(nV) {
      this.$root.$emit('setModelingResultsStyle', this.type, nV, this.isLabelsVisible);
    },
  },
  methods: {
    changeLayerVisibility() {
      this.$root.$emit('changeLayerVisibility', { layerId: this.type, visible: this.isVisible });
    },
    changeLabelsVisibility() {
      this.$root.$emit('setModelingResultsStyle', this.type, this.selectedParameter, !this.isLabelsVisible);
      this.isLabelsVisible = !this.isLabelsVisible;
    },
    prepareRanges(ranges) {
      return [...ranges, '>'].map((value, index, array) => {
        if (index === 0) {
          return `< ${value}`;
        } else if (index === array.length - 1) {
          return `${value} ${array[index - 1]}`;
        } else {
          return `${array[index - 1]} - ${value}`;
        }
      });
    },
  },
  mounted() {
    this.selectedParameter = this.computedParameterTypeRangesArray[0];
  },
};
</script>

<style lang="scss" scoped></style>
